import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useAllDrivers } from "../../hooks/driver-operations/useDriverQueries";
import { useCreateRide } from "../../hooks/ride-operations/useRideMutations";
import { useUserStore } from "../../stores";
import { useRideStore } from "../../stores";
import Map from "../shared/Map";

function ConfirmRide({ onModalClose, confirmObj }) {
  // Global state.
  const { userId } = useUserStore();
  const { setCurrentRide } = useRideStore();

  // Destructure the confirm prop.
  const {
    name: riderName,
    phone: riderPhone,
    passengers: ridePassengers,
    pickUp: ridePickUp,
    pickUpLat,
    pickUpLng,
    dropOff: rideDropOff,
    dropOffLat,
    dropOffLng,
    pickUpTime: ridePickUpTime,
    date: rideData,
    bidAmount,
    requestedDriverIds,
  } = confirmObj;

  // Use coordinates from Places Autocomplete (passed via confirmObj)
  const hasPickUpCoords = pickUpLat != null && pickUpLng != null;
  const hasDropOffCoords = dropOffLat != null && dropOffLng != null;
  const isWarning = !hasPickUpCoords || !hasDropOffCoords;

  const mapCoordinates = {
    pickUpLat: pickUpLat || null,
    pickUpLong: pickUpLng || null,
    dropOffLat: dropOffLat || null,
    dropOffLong: dropOffLng || null,
  };

  // Set up route navigation.
  const navigate = useNavigate();

  // Get the driver query.
  const { allDriversData, loadingAllDrivers } = useAllDrivers();

  // Get the create ride mutation.
  const { createRide, loadingCreateRide, errorCreateRide } = useCreateRide();

  // Function for finding requested driver's name from all drivers query.
  const hasRequestedDrivers =
    requestedDriverIds && requestedDriverIds.length > 0;

  const requestedDriverNames = useMemo(() => {
    if (!hasRequestedDrivers || !allDriversData?.getAllDrivers) {
      return [];
    }

    return requestedDriverIds
      .map((driverId) => {
        const driver = allDriversData.getAllDrivers.find(
          (d) => d.id === driverId
        );
        return driver ? driver.name : "Unknown Driver";
      })
      .filter((name) => name !== "Unknown Driver");
  }, [requestedDriverIds, allDriversData]);

  const handleConfirmRide = async () => {
    try {
      const response = await createRide({
        variables: {
          riderId: userId,
          riderName,
          phoneNumber: riderPhone,
          passengers: ridePassengers,
          pickUp: ridePickUp,
          pickUpLat: pickUpLat || null,
          pickUpLng: pickUpLng || null,
          dropOff: rideDropOff,
          dropOffLat: dropOffLat || null,
          dropOffLng: dropOffLng || null,
          pickUpTime: ridePickUpTime,
          bidAmount: bidAmount,
          requestedDriverIds: requestedDriverIds || null,
        },
      });

      if (response.data?.createRide?.id) {
        setCurrentRide(response.data.createRide.id);
        onModalClose && onModalClose();
        navigate("/waitingforride");
      } else {
        console.error("No ride ID received from server");
      }
    } catch (error) {
      console.error("Error creating ride:", error);
    }
  };

  return (
    <div className="flex flex-col w-full">
      {/* Header */}
      <h3 className="text-white text-xl font-bold text-center mb-4">
        Confirm Your Ride
      </h3>

      {/* Warning Banner */}
      {isWarning && (
        <div className="bg-main-red-dark/20 border border-main-red-light/50 rounded-lg p-3 mb-3">
          <p className="text-main-red-light text-sm text-center">
            Addresses in red could not be found on the map. A driver might still
            accept your ride, but consider revising.
          </p>
        </div>
      )}

      {/* Bid Banner */}
      {bidAmount > 0 && (
        <div className="bg-main-gold-dark/20 border border-main-gold-light/40 rounded-lg p-3 mb-3 text-center">
          <p className="text-main-gold-light text-xs uppercase tracking-wide mb-1">
            Your Tip
          </p>
          <p className="text-main-gold-light text-2xl font-bold">
            ${bidAmount.toFixed(2)}
          </p>
        </div>
      )}

      {/* Requested Driver Banner - always visible */}
      <div
        className={`rounded-lg p-3 mb-3 text-center border ${
          hasRequestedDrivers
            ? "bg-main-teal-dark/20 border-main-teal-light/40"
            : "bg-white/5 border-white/10"
        }`}
      >
        <p
          className={`text-xs uppercase tracking-wide mb-1 ${
            hasRequestedDrivers
              ? "text-main-teal-light"
              : "text-main-light-highlight/70"
          }`}
        >
          Requested Driver{hasRequestedDrivers && requestedDriverIds.length > 1 ? "s" : ""}
        </p>
        <p
          className={`font-semibold ${
            hasRequestedDrivers ? "text-white" : "text-main-light-highlight"
          }`}
        >
          {hasRequestedDrivers
            ? loadingAllDrivers
              ? "Loading..."
              : requestedDriverNames.join(", ")
            : "Next Available Driver"}
        </p>
      </div>

      {/* Rider Info Card */}
      <div className="bg-white/5 border border-white/10 rounded-lg p-4 mb-3">
        <div className="grid grid-cols-2 gap-3 text-center">
          <div>
            <p className="text-main-light-highlight/70 text-xs uppercase tracking-wide">
              Name
            </p>
            <p className="text-white font-medium text-sm">{riderName}</p>
          </div>
          <div>
            <p className="text-main-light-highlight/70 text-xs uppercase tracking-wide">
              Phone
            </p>
            <p className="text-white font-medium text-sm">{riderPhone}</p>
          </div>
          <div>
            <p className="text-main-light-highlight/70 text-xs uppercase tracking-wide">
              Passengers
            </p>
            <p className="text-white font-medium text-sm">{ridePassengers}</p>
          </div>
          <div>
            <p className="text-main-light-highlight/70 text-xs uppercase tracking-wide">
              Pickup Time
            </p>
            <p className="text-white font-medium text-sm">{ridePickUpTime}</p>
          </div>
        </div>
      </div>

      {/* Location Card */}
      <div className="bg-white/5 border border-white/10 rounded-lg p-4 mb-3">
        <div className="flex">
          {/* Timeline column */}
          <div className="flex flex-col items-center mr-3">
            <span className="w-3 h-3 bg-main-green-light rounded-full"></span>
            <div className="w-0.5 bg-white/20 flex-1 my-1"></div>
            <span className="w-3 h-3 bg-main-red-light rounded-full"></span>
          </div>

          {/* Content column */}
          <div className="flex flex-col flex-1 justify-between">
            <div className="pb-3">
              <p className="text-main-green-light text-xs uppercase tracking-wide">
                Pick Up
              </p>
              <p
                className={`font-medium text-sm text-center ${
                  hasPickUpCoords ? "text-white" : "text-main-red-light"
                }`}
              >
                {ridePickUp}
              </p>
            </div>
            <div className="pt-3">
              <p className="text-main-red-light text-xs uppercase tracking-wide">
                Drop Off
              </p>
              <p
                className={`font-medium text-sm text-center ${
                  hasDropOffCoords ? "text-white" : "text-main-red-light"
                }`}
              >
                {rideDropOff}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Map */}
      <div className="w-full h-40 md:h-52 rounded-lg overflow-hidden border border-white/10 mb-3">
        <Map
          coordinatesData={mapCoordinates}
          markerDepartureToggle={hasPickUpCoords}
          markerDestinationToggle={hasDropOffCoords}
          readOnly
        />
      </div>

      {/* Status Messages */}
      {loadingCreateRide && (
        <p className="text-main-blue-light text-center text-sm mb-3 animate-pulse">
          Creating your ride request...
        </p>
      )}
      {errorCreateRide && (
        <div className="bg-main-red-dark/20 border border-main-red-light/50 rounded-lg p-3 mb-3">
          <p className="text-main-red-light text-center text-sm">
            Error: {errorCreateRide.message}
          </p>
        </div>
      )}

      {/* Action Buttons */}
      <div className="flex flex-col items-center gap-2 mt-1">
        <button
          type="button"
          className="btn btn-green btn-md disabled:opacity-50 disabled:cursor-not-allowed"
          onClick={handleConfirmRide}
          disabled={loadingCreateRide}
        >
          {loadingCreateRide ? "Processing..." : "Confirm Ride"}
        </button>
        <button
          type="button"
          className="btn btn-red btn-md"
          onClick={onModalClose}
        >
          Cancel
        </button>
      </div>
    </div>
  );
}

export default ConfirmRide;
